import {
	PageHeader,
	SetlistTagsInput,
	SongItem,
	SongPicker,
	SongViewer,
	TextInput,
	TextItem,
	TextPicker,
} from "@/components";
import {
	useAllSetlists,
	useSetShowTextOnSlide,
	useSetlist,
} from "@/hooks/queries/useSetlistQueries";
import { useTaggedSong } from "@/hooks/queries/useSongQueries";
import { useIsMobile } from "@/hooks/useIsMobile";
import { queryKeys } from "@/utils/queryKeys";
import { sortSetlistItems } from "@/utils/setlistOrder";
import {
	type AllSetlists,
	setlistItemAppendMutation,
	setlistItemDeleteMutation,
	setlistItemPositionMutation,
	setlistNameMutation,
	setlistNameQuery,
	setlistTagsMutation,
	setlistTextItemMutation,
} from "@/utils/supabase";
import {
	DndContext,
	type DragEndEvent,
	MouseSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
	SortableContext,
	arrayMove,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Switch } from "@headlessui/react";
import {
	DocumentTextIcon,
	MusicalNoteIcon,
	PencilIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

type SetlistItem = NonNullable<ReturnType<typeof useSetlist>["data"]>[number];

function SortableSetlistItem({
	item,
	selected,
	onSelect,
	onDelete,
}: {
	item: SetlistItem;
	selected: boolean;
	onSelect: (id: number) => void;
	onDelete: (id: number) => void;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: item.id });

	return (
		<li
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
				WebkitTouchCallout: "none",
			}}
			className={clsx(
				"flex pr-2 select-none hover:bg-jubilateBlue-100 dark:hover:bg-slate-600 items-center",
				isDragging ? "cursor-grabbing" : "cursor-grab",
				selected && "bg-gray-100 dark:bg-slate-700",
			)}
			{...attributes}
			{...listeners}
		>
			<button
				type="button"
				className="w-full text-left min-w-0 flex items-center gap-2 pl-2"
				onClick={() => onSelect(item.id)}
			>
				{item.songs && (
					<>
						<MusicalNoteIcon className="w-5 shrink-0 text-jubilateBlue-500 dark:text-jubilateBlue-400" />
						<SongItem song={item.songs} hover={false} />
					</>
				)}
				{item.texts && (
					<>
						<DocumentTextIcon className="w-5 shrink-0 text-jubilateGreen-500" />
						<TextItem text={item.texts} hover={false} />
					</>
				)}
				{item.text != null && (
					<>
						<PencilIcon className="w-5 shrink-0 text-jubilatePurple-400" />
						<p className="px-2 text-black dark:text-white truncate">
							{item.text.split("\n")[0] || "Texte libre (vide)"}
						</p>
					</>
				)}
			</button>
			<div className="flex items-center h-fit w-fit my-2 mx-2">
				<button type="button" onClick={() => onDelete(item.id)}>
					<XMarkIcon className="size-8.5 shrink-0 rounded-full bg-jubilateRed-400 hover:bg-jubilateRed-500 p-1 text-white transition" />
				</button>
			</div>
		</li>
	);
}

function ShowTextOnSlideSwitch({
	checked,
	onChange,
}: {
	checked: boolean;
	onChange: (value: boolean) => void;
}) {
	return (
		<div className="flex items-center justify-between gap-4 pb-4">
			<div>
				<p className="font-medium text-gray-900 dark:text-gray-100">
					Afficher le texte sur l'écran
				</p>
				<p className="text-sm text-gray-500 dark:text-gray-400">
					{checked
						? "Le texte s'affiche dans le diaporama."
						: "Le diaporama affiche la croix."}
				</p>
			</div>
			<Switch
				checked={checked}
				onChange={onChange}
				className={clsx(
					"relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors",
					checked ? "bg-jubilateBlue-500" : "bg-gray-300 dark:bg-gray-600",
				)}
			>
				<span
					className={clsx(
						"inline-block h-5 w-5 translate-y-0.5 rounded-full bg-white transition-transform",
						checked ? "translate-x-5.5" : "translate-x-0.5",
					)}
				/>
			</Switch>
		</div>
	);
}

const SetlistEditor = () => {
	const { setlistId } = useParams();
	const queryClient = useQueryClient();
	const isMobile = useIsMobile();

	const { data: setlistData } = useSetlist(setlistId);
	const setlist = useMemo(
		() => (setlistData ? sortSetlistItems(setlistData) : null),
		[setlistData],
	);

	const [selectedItem, setSelectedItem] = useState<number>();
	const [setlistName, setSetlistName] = useState<string>("");
	const [isSongPickerOpen, setIsSongPickerOpen] = useState(false);
	const [isTextPickerOpen, setIsTextPickerOpen] = useState(false);
	const [textNewValue, setTextNewValue] = useState<string | null>(null);
	const [orderedIds, setOrderedIds] = useState<number[]>([]);

	// Mouse: a plain click still selects the row; only a drag past a few px reorders it.
	// Touch: matches the app's long-press convention (see useLongPress) before a drag starts,
	// so a normal vertical swipe keeps scrolling the list.
	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
		useSensor(TouchSensor, {
			activationConstraint: { delay: 500, tolerance: 10 },
		}),
	);

	// Sync local order whenever the server-backed setlist changes (initial load, refetch after a mutation).
	useEffect(() => {
		if (setlist) setOrderedIds(setlist.map((item) => item.id));
	}, [setlist]);

	const orderedSetlist = useMemo(() => {
		if (!setlist) return null;
		const byId = new Map(setlist.map((item) => [item.id, item]));
		return orderedIds
			.map((id) => byId.get(id))
			.filter((item): item is NonNullable<typeof item> => item !== undefined);
	}, [setlist, orderedIds]);

	const handleSelectItem = (id: number) => {
		setSelectedItem(id);
		const textValue = setlist?.find((item) => item.id === id)?.text;
		if (textValue !== undefined && textValue !== null)
			setTextNewValue(textValue);
		else setTextNewValue(null);
	};

	const invalidateSetlist = useCallback(() => {
		if (!setlistId) return;
		queryClient.invalidateQueries({
			queryKey: queryKeys.setlists.detail(setlistId),
		});
	}, [setlistId, queryClient]);

	// Load setlist name on mount
	useEffect(() => {
		if (!setlistId) return;
		setlistNameQuery(setlistId).then(({ data }) => {
			if (data?.name) setSetlistName(data.name);
		});
	}, [setlistId]);

	const handleSaveSetlistName = () => {
		if (!setlistId) return;
		setlistNameMutation(setlistId, setlistName).then(({ error }) => {
			if (error) toast.error("Erreur lors de l'enregistrement du nom");
		});
	};

	const { data: allSetlists = [] } = useAllSetlists();
	const setlistTags =
		allSetlists.find((s) => String(s.id) === setlistId)?.tags ?? [];
	const tagSuggestions = useMemo(
		() =>
			[...new Set(allSetlists.flatMap((s) => s.tags))].sort((a, b) =>
				a.localeCompare(b, "fr"),
			),
		[allSetlists],
	);

	const tagsMutation = useMutation({
		mutationFn: async (tags: string[]) => {
			const { error } = await setlistTagsMutation(Number(setlistId), tags);
			if (error) throw error;
		},
		onMutate: async (tags) => {
			const listKey = queryKeys.setlists.list();
			await queryClient.cancelQueries({ queryKey: listKey });
			const previous = queryClient.getQueryData<AllSetlists>(listKey);
			queryClient.setQueryData<AllSetlists>(listKey, (old) =>
				old?.map((s) => (String(s.id) === setlistId ? { ...s, tags } : s)),
			);
			return { previous };
		},
		onError: (_error, _tags, context) => {
			queryClient.setQueryData(queryKeys.setlists.list(), context?.previous);
			toast.error("Erreur lors de l'enregistrement des tags");
		},
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.setlists.list() }),
	});

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!setlist || !setlistId || !over || active.id === over.id) return;

		const oldIndex = orderedIds.indexOf(Number(active.id));
		const newIndex = orderedIds.indexOf(Number(over.id));
		if (oldIndex === -1 || newIndex === -1) return;

		const newOrderedIds = arrayMove(orderedIds, oldIndex, newIndex);
		setOrderedIds(newOrderedIds);

		const positionById = new Map(
			setlist.map((item) => [item.id, item.position]),
		);
		const mutations = newOrderedIds
			.map((id, position) => ({ id, position }))
			.filter(({ id, position }) => positionById.get(id) !== position)
			.map(({ id, position }) =>
				setlistItemPositionMutation(setlistId, id, position),
			);
		Promise.all(mutations).then((results) => {
			if (results.some(({ error }) => error))
				toast.error("Erreur lors de la réorganisation");
			invalidateSetlist();
		});
	};

	const handleDelete = (id: number) => {
		if (!setlist || !setlistId) return;
		setlistItemDeleteMutation(setlistId, id).then(({ error }) => {
			if (error) {
				toast.error("Erreur lors de la suppression");
				return;
			}
			invalidateSetlist();
		});
	};

	const handleClose = (songId?: number) => {
		setIsSongPickerOpen(false);
		if (!songId || !setlistId || setlist === null) return;
		setlistItemAppendMutation(
			setlist.length,
			Number(setlistId),
			songId,
			null,
			null,
		).then(({ error }) => {
			if (error) {
				toast.error("Erreur lors de l'ajout du chant");
				return;
			}
			invalidateSetlist();
		});
	};

	const handleCloseTextPicker = (textId?: number) => {
		setIsTextPickerOpen(false);
		if (!textId || !setlistId || setlist === null) return;
		setlistItemAppendMutation(
			setlist.length,
			Number(setlistId),
			null,
			null,
			textId,
		).then(({ error }) => {
			if (error) {
				toast.error("Erreur lors de l'ajout du texte");
				return;
			}
			invalidateSetlist();
		});
	};

	const handleSaveNewText = useCallback(() => {
		if (!setlistId || !selectedItem) return;
		setlistTextItemMutation(setlistId, selectedItem, textNewValue ?? "").then(
			({ error }) => {
				if (error) {
					toast.error("Erreur lors de l'enregistrement du texte");
					return;
				}
				invalidateSetlist();
			},
		);
	}, [setlistId, selectedItem, textNewValue, invalidateSetlist]);

	const handleNewTextItem = () => {
		if (!setlistId || setlist === null) return;
		setlistItemAppendMutation(
			setlist.length,
			Number(setlistId),
			null,
			"",
			null,
		).then(({ error }) => {
			if (error) {
				toast.error("Erreur lors de la création du texte libre");
				return;
			}
			invalidateSetlist();
		});
	};

	useEffect(() => {
		if (textNewValue === null) return;
		const currentSelectedItem = selectedItem; // Capture the current selectedItem
		const timeout = setTimeout(() => {
			if (!setlistId || !currentSelectedItem) return;
			setlistTextItemMutation(
				setlistId,
				currentSelectedItem,
				textNewValue ?? "",
			).then(({ error }) => {
				if (error) {
					toast.error("Erreur lors de l'enregistrement du texte");
					return;
				}
				invalidateSetlist();
			});
		}, 1000);
		return () => clearTimeout(timeout);
	}, [textNewValue, selectedItem, setlistId, invalidateSetlist]);

	// The setlist join no longer carries strophes — the preview reads the song
	// body from its single owner (queryKeys.songs.detail), so it shows the same
	// notes and lyrics as everywhere else.
	const selectedSongId = setlist?.find((item) => item.id === selectedItem)
		?.songs?.id;
	const { data: selectedSong } = useTaggedSong(selectedSongId);
	const selectedSetlistItem = setlist?.find((item) => item.id === selectedItem);
	const selectedText = selectedSetlistItem?.texts;

	const setShowTextOnSlide = useSetShowTextOnSlide(setlistId);
	const showTextOnSlideSwitch = selectedSetlistItem &&
		!selectedSetlistItem.songs && (
			<ShowTextOnSlideSwitch
				checked={selectedSetlistItem.show_text_on_slide}
				onChange={(showTextOnSlide) =>
					setShowTextOnSlide.mutate({
						itemId: selectedSetlistItem.id,
						showTextOnSlide,
					})
				}
			/>
		);

	return (
		<div className="flex flex-col h-screen">
			<PageHeader
				variant="detail"
				title={setlistName ? `Modifier « ${setlistName} »` : "Chargement..."}
			/>
			{setlist ? (
				<div className="grid grid-cols-3 flex-1 min-h-0">
					{/* Le padding bottom sert à ne pas cacher le dernier boutton supprimer de la liste avec le bouton menu flottant */}
					<ul className="col-span-3 md:col-span-1 border-r-2 border-gray-200 dark:border-gray-600 h-full min-h-0 overflow-y-auto pb-12">
						<li className="px-4 py-2">
							<TextInput
								label="Nom"
								value={setlistName}
								onChange={setSetlistName}
								onBlur={handleSaveSetlistName}
							/>
						</li>
						<li className="px-4 pb-2">
							<SetlistTagsInput
								value={setlistTags}
								suggestions={tagSuggestions}
								onChange={(tags) => tagsMutation.mutate(tags)}
							/>
						</li>
						<li className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-600 flex items-center justify-center flex-wrap gap-3 p-2">
							<button
								type="button"
								className="px-4 py-2 bg-jubilateBlue-500 hover:bg-jubilateBlue-600 dark:bg-jubilateBlue-400 dark:hover:bg-jubilateBlue-300 text-white rounded-full transition"
								onClick={() => setIsSongPickerOpen(true)}
							>
								+ Chant
							</button>
							<button
								type="button"
								className="px-4 py-2 bg-jubilateGreen-400 hover:bg-jubilateGreen-500 text-white rounded-full transition"
								onClick={() => setIsTextPickerOpen(true)}
							>
								+ Texte existant
							</button>
							<button
								type="button"
								className="px-4 py-2 bg-jubilatePurple-400 hover:bg-jubilatePurple-500 text-white rounded-full transition"
								onClick={() => handleNewTextItem()}
							>
								+ Texte libre
							</button>
						</li>
						{orderedSetlist?.length === 0 && (
							<li className="text-center text-gray-500 dark:text-gray-400 py-8 px-4">
								Cette setlist est vide. Ajoutez un chant ou un texte pour
								commencer.
							</li>
						)}
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToVerticalAxis]}
						>
							<SortableContext
								items={orderedIds}
								strategy={verticalListSortingStrategy}
							>
								{orderedSetlist?.map((item) => (
									<SortableSetlistItem
										key={item.id}
										item={item}
										selected={selectedItem === item.id}
										onSelect={handleSelectItem}
										onDelete={handleDelete}
									/>
								))}
							</SortableContext>
						</DndContext>
					</ul>
					{selectedSong && (
						<div className="col-span-2 h-full min-h-0 overflow-y-auto hidden md:block">
							<SongViewer song={selectedSong} />
						</div>
					)}
					{selectedText && (
						<div className="col-span-2 h-full min-h-0 overflow-y-auto hidden md:block p-6">
							{showTextOnSlideSwitch}
							<div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
								<h2 className="text-2xl font-bold mb-4 text-black dark:text-white">
									{selectedText.title}
								</h2>
								<div className="whitespace-pre-wrap text-black dark:text-white leading-relaxed">
									{selectedText.content}
								</div>
							</div>
						</div>
					)}
					{textNewValue !== null && (
						<div className="p-4 col-span-2 h-full min-h-0 hidden md:flex flex-col">
							{showTextOnSlideSwitch}
							<textarea
								onChange={(value) => setTextNewValue(value.currentTarget.value)}
								className="grow w-full outline-hidden bg-transparent text-black dark:text-white"
								onBlur={() => handleSaveNewText()}
								placeholder="Probablement parler de Jésus..."
								value={textNewValue}
							/>
						</div>
					)}
				</div>
			) : (
				<p className="p-4">Chargement...</p>
			)}
			{isMobile && selectedItem !== undefined && (
				// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
				<div
					className="bg-black/50 fixed inset-0 z-50 flex justify-center items-center"
					onClick={() => setSelectedItem(undefined)}
				>
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
					<div
						className="bg-white dark:bg-gray-800 rounded-lg size-11/12 overflow-y-auto"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-end p-2 sticky top-0 bg-white dark:bg-gray-800">
							<button type="button" onClick={() => setSelectedItem(undefined)}>
								<XMarkIcon className="w-8 text-black dark:text-white" />
							</button>
						</div>
						{selectedSong && <SongViewer showTitle song={selectedSong} />}
						{showTextOnSlideSwitch && (
							<div className="px-4">{showTextOnSlideSwitch}</div>
						)}
						{selectedText && (
							<div className="p-6">
								<h2 className="text-2xl font-bold mb-4 text-black dark:text-white">
									{selectedText.title}
								</h2>
								<div className="whitespace-pre-wrap text-black dark:text-white leading-relaxed">
									{selectedText.content}
								</div>
							</div>
						)}
						{textNewValue !== null && (
							<div className="p-4">
								<textarea
									onChange={(value) =>
										setTextNewValue(value.currentTarget.value)
									}
									className="h-64 w-full outline-hidden bg-transparent text-black dark:text-white"
									onBlur={() => handleSaveNewText()}
									placeholder="Probablement parler de Jésus..."
									value={textNewValue}
								/>
							</div>
						)}
					</div>
				</div>
			)}
			{isSongPickerOpen && <SongPicker handleClose={handleClose} />}
			{isTextPickerOpen && <TextPicker handleClose={handleCloseTextPicker} />}
		</div>
	);
};

export { SetlistEditor };
